/**
 * Customers API – mirrors src/services/customers.ts in vin-pulse-os-api.
 *
 * Customer self-service endpoints used by ecommerce:
 *   GET   /api/v1/customers/me        – own profile + metrics
 *   PATCH /api/v1/customers/:id       – limited self-update (full_name,
 *                                       contact_phone, address fields)
 *
 * Backend enforces field-level scoping for the Customer role: only the
 * fields listed in `customerSelfFields` of the routes file are accepted.
 * `UpdateCustomerInput` here is intentionally limited to that whitelist to
 * keep the contract in sync.
 */

import { apiClient } from './apiClient';

export type CustomerStatus = 'active' | 'inactive' | 'suspended';

export interface CustomerMetrics {
  total_revenue: number;
  order_count: number;
  last_order_at: string | null;
  outstanding_balance: number;
  avg_order_value: number;
}

/**
 * users.credit_limit — wired into GET /customers/me in vin-pulse-os-api
 * commit 12043e9 (VP-CUST-001, backend half). Nullable: not every user row
 * has a credit limit set.
 */
export type CreditLimit = number | null;

export interface Customer {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  customer_status: CustomerStatus | null;

  store_name: string | null;
  llc_name: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;

  sales_rep_id: number | null;
  sales_rep_name: string | null;

  pricing_tier_id: number | null;
  pricing_tier_name: string | null;

  credit_limit: CreditLimit;

  metrics: CustomerMetrics;

  created_at: string;
  updated_at: string;
}

/**
 * Whitelist of fields the Customer role is allowed to PATCH on its own
 * profile. Matches `customerSelfFields` in the API route so a request that
 * passes type-check here will also pass the backend's field-level guard.
 */
export interface UpdateCustomerInput {
  full_name?: string;
  contact_phone?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

export async function getMyCustomerProfile(): Promise<Customer> {
  return apiClient.get<Customer>('/customers/me');
}

export async function updateMyCustomerProfile(
  customerId: number,
  input: UpdateCustomerInput
): Promise<Customer> {
  return apiClient.patch<Customer>(`/customers/${customerId}`, input);
}
